import React from 'react';
import { Box, Grid, Typography } from '@mui/material';
import { SchedulerConfigurationData } from '../../../../api';
import { useLanguage } from '../../../../common/GeneralUtils';

export enum SchedulerGridDisplayMode {
  FULL = 'full', // Full interactive mode with labels and help text
  COMPACT = 'compact', // Compact mode for popover display
  INLINE = 'inline', // Ultra-compact inline mode for unavailability box
}

interface SchedulerResourceGridProps {
  schedulerConfig: SchedulerConfigurationData;
  blockedMask: string;
  onChange?: (mask: string) => void;
  disabled?: boolean;
  readOnly?: boolean;
  displayMode?: SchedulerGridDisplayMode;
}

export const SchedulerResourceGrid: React.FC<SchedulerResourceGridProps> = ({
  schedulerConfig,
  blockedMask,
  onChange,
  disabled = false,
  readOnly = false,
  displayMode = SchedulerGridDisplayMode.FULL,
}) => {
  const language = useLanguage();

  const resourceCount = schedulerConfig.resource_count;
  const currentMask = blockedMask.padEnd(resourceCount, '0').substring(0, resourceCount);

  const toggleResourceBlock = (position: number) => {
    if (disabled || readOnly || !onChange) return;

    const maskArray = currentMask.split('');
    const reversedPosition = resourceCount - 1 - position;
    maskArray[reversedPosition] = maskArray[reversedPosition] === '1' ? '0' : '1';
    const newMask = maskArray.join('');
    onChange(newMask);
  };

  const renderResourcePositions = () => {
    const positions = [];
    for (let i = 0; i < resourceCount; i++) {
      const reversedPosition = resourceCount - 1 - i;
      const isBlocked = currentMask[reversedPosition] === '1';
      positions.push(
        <Box
          key={i}
          onClick={() => toggleResourceBlock(i)}
          sx={{
            width:
              displayMode === SchedulerGridDisplayMode.INLINE
                ? 16
                : displayMode === SchedulerGridDisplayMode.COMPACT
                ? 32
                : 40,
            height:
              displayMode === SchedulerGridDisplayMode.INLINE
                ? 16
                : displayMode === SchedulerGridDisplayMode.COMPACT
                ? 32
                : 40,
            border: displayMode === SchedulerGridDisplayMode.INLINE ? '1px solid #ccc' : '2px solid #ccc',
            borderRadius: displayMode === SchedulerGridDisplayMode.INLINE ? '2px' : '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: disabled || readOnly ? 'default' : 'pointer',
            backgroundColor: isBlocked ? '#f44336' : '#4caf50',
            color: 'white',
            fontWeight: 'bold',
            fontSize:
              displayMode === SchedulerGridDisplayMode.INLINE
                ? '8px'
                : displayMode === SchedulerGridDisplayMode.COMPACT
                ? '12px'
                : '14px',
            '&:hover':
              disabled || readOnly
                ? {}
                : {
                    opacity: 0.8,
                  },
          }}>
          {displayMode === SchedulerGridDisplayMode.INLINE ? '' : i + 1}
        </Box>,
      );
    }
    return positions;
  };

  if (displayMode === SchedulerGridDisplayMode.INLINE) {
    return (
      <Grid container alignItems="center" gap={0.25} flexWrap="nowrap">
        <Typography variant="caption" sx={{ fontSize: '11px', mr: 0.5 }}>
          {schedulerConfig.label[language] || schedulerConfig.label.en || `Config ${schedulerConfig.id}`}:
        </Typography>
        <Grid container gap={0.25} flexWrap="nowrap">
          {renderResourcePositions()}
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid
      container
      flexDirection="column"
      gap={displayMode === SchedulerGridDisplayMode.COMPACT ? 1 : 2}
      mb={displayMode === SchedulerGridDisplayMode.COMPACT ? 1 : 2}>
      <Typography
        variant={displayMode === SchedulerGridDisplayMode.COMPACT ? 'body2' : 'subtitle1'}
        fontWeight={displayMode === SchedulerGridDisplayMode.COMPACT ? '600' : '400'}>
        {schedulerConfig.label[language] || schedulerConfig.label.en || `Config ${schedulerConfig.id}`}
      </Typography>

      <Grid
        container
        gap={displayMode === SchedulerGridDisplayMode.COMPACT ? 0.5 : 1}
        flexWrap="wrap"
        maxWidth={displayMode === SchedulerGridDisplayMode.COMPACT ? 300 : 400}>
        {renderResourcePositions()}
      </Grid>
    </Grid>
  );
};
